import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';

import { useComboboxContext } from '@/components/Form/pickers/primitives/combobox/ComboboxContext';
import { cn } from '@/lib/utils';

export type ComboboxContentProps = ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
  >;

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  alignOffset = 0,
  anchor: anchorProp,
  ...props
}: ComboboxContentProps) => {
  const { chipsAnchor, inputAnchor } = useComboboxContext();
  const anchor = anchorProp ?? chipsAnchor ?? inputAnchor;

  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-1101"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!chipsAnchor}
          className={cn(
            'group/combobox-content',
            'relative isolate z-1101 max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin)',
            'overflow-hidden data-[align-trigger=true]:animate-none',
            'border-gray-900 bg-white text-gray-900 duration-100',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[size=right]:slide-in-from-left-2 data-[size=top]:slide-in-from-bottom-2',
            'data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2',
            '**:data-[slot=input-group]:m-1 **:data-[slot=input-group]:mb-0 **:data-[slot=input-group]:h-8 **:data-[slot=input-group]:border-gray-600 **:data-[slot=input-group]:bg-white/30 **:data-[slot=input-group]:shadow-none',
            'rounded-md shadow-md ring-1 ring-gray-900/10',
            'data-ending-style:opacity-0 data-starting-style:opacity-0',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

export default ComboboxContent;
